import { Calculator } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ChecklistComponent, ChecklistScalarValue } from "@/types/quote.types";
import {
  type CalculationInputs,
  evaluateCalculation,
  formatCalculatedValue,
  getInputLabel,
  parseCalculationInputs,
} from "@/utils/calculation";

interface CalculationInputProps {
  component: ChecklistComponent;
  value: ChecklistScalarValue;
  onChange: (value: ChecklistScalarValue) => void;
}

/**
 * CalculationInput component
 *
 * Renders input fields for calculation (length, width, height, etc.)
 * and displays the calculated result in real-time
 *
 * Supports two modes:
 * 1. With formula: Shows inputs and calculated result (e.g., area = length × width)
 * 2. Without formula: Just shows inputs for data collection (e.g., room measurements)
 */
export function CalculationInput({ component, value, onChange }: CalculationInputProps) {
  const inputFields = useMemo(() => {
    return parseCalculationInputs(component.calculation_inputs);
  }, [component.calculation_inputs]);

  // Parse existing value as calculation inputs object
  const [inputs, setInputs] = useState<CalculationInputs>(() => {
    // Default values for wall-by-wall calculation
    const defaults: CalculationInputs = {
      wastagePercent: 10, // Default 10% wastage
      includeFloor: 0, // Default floor not included
      floorLength: 0, // Default floor length
      floorWidth: 0, // Default floor width
      numWalls: 4, // Default 4 walls (standard bathroom)
    };

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      // Merge existing value with defaults (existing values take precedence)
      return { ...defaults, ...(value as unknown as CalculationInputs) };
    }

    return defaults;
  });

  // Track number of walls for dynamic rendering
  const numWalls = inputs.numWalls ?? 4;

  // Calculate result whenever inputs change
  const calculatedResult = useMemo(() => {
    if (!component.calculation_formula) {
      return null;
    }

    const result = evaluateCalculation(component.calculation_formula, inputs);
    console.log("[CalculationInput] Formula evaluated:", {
      formula: component.calculation_formula,
      inputs,
      result,
    });
    return result;
  }, [component.calculation_formula, inputs]);

  // Update parent component whenever inputs or calculated result changes
  useEffect(() => {
    // Store both the inputs and the calculated result
    const outputValue = {
      ...inputs,
      _calculated: calculatedResult,
    };

    onChange(outputValue as unknown as ChecklistScalarValue);
  }, [inputs, calculatedResult, onChange]);

  const handleInputChange = (fieldName: string, rawValue: string) => {
    const numericValue = rawValue === "" ? undefined : Number(rawValue);

    console.log(`[CalculationInput] ${fieldName} changed:`, numericValue);
    setInputs((prev) => ({
      ...prev,
      [fieldName]: numericValue,
    }));
  };

  const handleCheckboxChange = (fieldName: string, checked: boolean) => {
    const numericValue = checked ? 1 : 0;

    console.log(`[CalculationInput] ${fieldName} checkbox changed:`, numericValue);
    setInputs((prev) => ({
      ...prev,
      [fieldName]: numericValue,
    }));
  };

  const isCheckboxField = (fieldName: string): boolean => {
    return fieldName === "includeFloor";
  };

  const isWallField = (fieldName: string): boolean => {
    return /^wall\d+(Length|Height)$/.test(fieldName);
  };

  const decimalPlaces = component.calculation_decimal_places ?? 2;
  const displayUnit = component.calculation_display_unit;

  // Generate dynamic wall input fields based on numWalls
  const generateWallFields = () => {
    const wallFields: JSX.Element[] = [];
    for (let i = 1; i <= numWalls; i++) {
      wallFields.push(
        <div key={`wall${i}Length`} className="space-y-1">
          <Label className="text-sm font-medium">Wall {i} Length (m)</Label>
          <Input
            type="number"
            inputMode="decimal"
            value={inputs[`wall${i}Length`] ?? ""}
            onChange={(e) => handleInputChange(`wall${i}Length`, e.target.value)}
            placeholder="0"
            min={0}
            step="0.01"
          />
        </div>,
        <div key={`wall${i}Height`} className="space-y-1">
          <Label className="text-sm font-medium">Wall {i} Height (m)</Label>
          <Input
            type="number"
            inputMode="decimal"
            value={inputs[`wall${i}Height`] ?? ""}
            onChange={(e) => handleInputChange(`wall${i}Height`, e.target.value)}
            placeholder="0"
            min={0}
            step="0.01"
          />
        </div>
      );
    }
    return wallFields;
  };

  return (
    <div className="space-y-4">
      {/* Number of Walls Control */}
      {inputFields.includes("numWalls") && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Label className="text-sm font-medium mb-2 block">Number of Walls</Label>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              inputMode="numeric"
              value={numWalls}
              onChange={(e) => handleInputChange("numWalls", e.target.value)}
              min={1}
              max={10}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">
              (Adjust for L-shaped or irregular bathrooms)
            </span>
          </div>
        </div>
      )}

      {/* Wall Input Fields */}
      {inputFields.some(isWallField) && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Wall Measurements</h4>
          <div className="grid grid-cols-2 gap-4">{generateWallFields()}</div>
        </div>
      )}

      {/* Floor Input Fields */}
      {(inputFields.includes("floorLength") || inputFields.includes("floorWidth")) && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Floor Measurements</h4>
          <div className="grid grid-cols-2 gap-4">
            {inputFields.includes("includeFloor") && (
              <div className="flex items-center space-x-2 col-span-2">
                <Checkbox
                  id="calc-includeFloor"
                  checked={inputs.includeFloor === 1}
                  onCheckedChange={(checked) => handleCheckboxChange("includeFloor", checked === true)}
                />
                <Label htmlFor="calc-includeFloor" className="text-sm font-medium cursor-pointer">
                  Include Floor in Calculation
                </Label>
              </div>
            )}
            {inputFields.includes("floorLength") && (
              <div className="space-y-1">
                <Label className="text-sm font-medium">Floor Length (m)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={inputs.floorLength ?? ""}
                  onChange={(e) => handleInputChange("floorLength", e.target.value)}
                  placeholder="0"
                  min={0}
                  step="0.01"
                />
              </div>
            )}
            {inputFields.includes("floorWidth") && (
              <div className="space-y-1">
                <Label className="text-sm font-medium">Floor Width (m)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={inputs.floorWidth ?? ""}
                  onChange={(e) => handleInputChange("floorWidth", e.target.value)}
                  placeholder="0"
                  min={0}
                  step="0.01"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Other Input Fields (wastage, tile size, etc.) */}
      <div className="grid grid-cols-2 gap-4">
        {inputFields
          .filter((fieldName) => !isWallField(fieldName) && !isCheckboxField(fieldName) && fieldName !== "floorLength" && fieldName !== "floorWidth" && fieldName !== "numWalls")
          .map((fieldName) => (
            <div key={fieldName} className="space-y-1">
              <Label className="text-sm font-medium">{getInputLabel(fieldName)}</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={inputs[fieldName] ?? ""}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                placeholder="0"
                min={0}
                step="any"
              />
            </div>
          ))}
      </div>

      {/* Calculated result display (only if formula exists) */}
      {component.calculation_formula && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white">
            <Calculator className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              Calculated {displayUnit ? `(${displayUnit})` : "Result"}
            </p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCalculatedValue(calculatedResult, displayUnit, decimalPlaces)}
            </p>
          </div>
          {calculatedResult !== null && component.base_price && component.base_price > 0 && (
            <Badge variant="outline" className="text-sm">
              ${(calculatedResult * component.base_price).toFixed(2)}
            </Badge>
          )}
        </div>
      )}

      {/* Show help text if provided */}
      {component.help_text && (
        <p className="text-xs text-muted-foreground">{component.help_text}</p>
      )}
    </div>
  );
}
